import React from 'react';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import classNames from 'classnames';

import { StoreProps } from 'store';
import {
  ChartViewData,
  DeclineReasonsData,
  IDictionary,
  IFormValues,
  InOutData,
  IOnChange,
  IValidateHandler,
  ModalChartTypes,
} from 'types/Analytics';

import ModalTitle from 'components/modal/components/ModalTitle';
import ModalContent from 'components/modal/components/ModalContent';
import ModalFooter from 'components/modal/components/ModalFooter';
import Button from 'components/ui/button';
import {
  AdvancedAnalyticsEntityType,
  AdvancedAnalyticsEntityTypes,
} from './components/AdvancedAnalyticsEntityTypes/AdvancedAnalyticsEntityTypes';
import SettingsForm from './components/settingsForm';
import { resolveWlValue } from 'helpers/wlResolver';
import ChartView from './components/chartView';
import NoContent from './components/NoContent';
import Loader from 'components/ui/loader';
import Icon from 'components/ui/icon';
import { InOutTable } from './components/InOutTable/InOutTable';
import { AdvancedAnalyticsTable } from 'components/modal/modalList/graphDetails/components/AdvancedAnalyticsTable/AdvancedAnalyticsTable';

import './graphDetails.scss';

const CHART_CLASSNAME = 'graph-details__chart-view';
const TABLE_CLASSNAME = 'graph-details__in-out-table';

export type ChartPreviewContentFn = (params: {
  options: ChartViewData | null | undefined;
  selectedType: AdvancedAnalyticsEntityType;
  getTranslate: IntlProps['getTranslate'];
  isChartPreviewVisible: boolean;
}) => React.ReactNode;

interface Props extends IntlProps, StoreProps {
  closeModal: () => void;
  saveChanges: () => void;
  setType: (type: AdvancedAnalyticsEntityType) => void;
  selectedType: AdvancedAnalyticsEntityType;
  values: IFormValues;
  options?: ChartViewData | null;
  validationErrors: { [key: string]: string };
  onChange: IOnChange;
  multiSelectItems: {
    projects: IDictionary;
    country: IDictionary;
    currency: IDictionary;
    paymentMethod: IDictionary;
    analyticsBrand: IDictionary;
    paymentMethodGroup: IDictionary;
    successRateTransactionType: IDictionary;
    vatState: IDictionary;
  };
  isLoading: boolean;
  titleToken: string;
  chartTypes: ModalChartTypes;
  validateHandler: IValidateHandler;
  formValidationErrors: { [key: string]: string };
  pristineFields: { [key: string]: boolean };
  submitButtonDisabledConditions: Array<{
    tooltipText: string;
    disabled: boolean;
  }>;
  formValidatedFields: string[];
  wlId?: number | null;
  showChartPreviewButton?: boolean;
  chartPreviewContent?: ChartPreviewContentFn;
}

interface State {
  isChartPreviewVisible: boolean;
}

class GraphDetails extends React.PureComponent<Props, State> {
  state: State = {
    isChartPreviewVisible: true,
  };

  toggleChartPreview = () => {
    this.setState((prev) => ({
      isChartPreviewVisible: !prev.isChartPreviewVisible,
    }));
  };

  render() {
    const {
      getTranslate,
      closeModal,
      saveChanges,
      titleToken,
      submitButtonDisabledConditions,
      showChartPreviewButton
    } = this.props;

    const submitDisabledConditions = submitButtonDisabledConditions.filter(
      ({ disabled }) => disabled
    );
    const tooltipTextSource = submitDisabledConditions.find(
      ({ tooltipText }) => tooltipText !== undefined
    );

    return (
      <div className='graph-details'>
        <ModalTitle customClass='graph-details__title'>
          {getTranslate(titleToken)}
        </ModalTitle>
        <ModalContent customClass='graph-details__content'>
          {this.renderContent()}
        </ModalContent>
        <ModalFooter customClass='graph-details__footer'>
          <Button
            status='outline'
            onClick={closeModal}
            text={getTranslate('common.cancel.button')}
          />
          <div className='graph-details__submit-button'>
            {tooltipTextSource !== undefined && (
              <Icon
                size={18}
                name='help_outline'
                className={classNames(
                  'payment-action__icon-info',
                  'settings-form__grid-element',
                  'graph-details__submit-tooltip-icon'
                )}
                data-place={'right'}
                tooltip={getTranslate(tooltipTextSource.tooltipText)}
                data-delay-hide='200'
              />
            )}
            {showChartPreviewButton && (
              <Button
                customClass='eto-chart-preview__toggle'
                onClick={this.toggleChartPreview}
                text={getTranslate('onboarding.form.preview.caption')}>
              </Button>
            )}
            <Button
              status='primary'
              onClick={saveChanges}
              text={getTranslate('common.saveContinue.button')}
              disabled={
                // Object.entries(pristineFields).every(
                //   ([_, value]) => value === true
                // ) ||
                // (Object.keys(formValidationErrors).length !== 0 &&
                //   Object.entries(formValidationErrors).every(
                //     ([_, value]) => value !== undefined
                //   )) ||
                submitDisabledConditions.some(({ disabled }) => disabled)
              }
            />
          </div>
        </ModalFooter>
      </div>
    );
  }

  get settingsForm() {
    const {
      values,
      validationErrors,
      selectedType,
      onChange,
      validateHandler,
      formValidationErrors,
      formValidatedFields,
      pristineFields,
      multiSelectItems,
      wlId,
    } = this.props;

    const WL_SETTINGS_FORM_CONFIGURATION = resolveWlValue((config) => (config as any).settingsFormWhatToRender, undefined);
    return (
      <SettingsForm
        values={values}
        validationErrors={validationErrors}
        selectedType={selectedType}
        onChange={onChange}
        validateHandler={validateHandler}
        formValidationErrors={formValidationErrors}
        formValidatedFields={formValidatedFields}
        pristineFields={pristineFields}
        wlId={wlId}
        renderContent={WL_SETTINGS_FORM_CONFIGURATION}
        {...multiSelectItems}
      />
    );
  }

  get chartView() {
    const { options } = this.props;

    return (
      options && (
        <ChartView
          data={options}
          className={CHART_CLASSNAME}
          shouldResize={false}
        />
      )
    );
  }

  whatToRender() {
    const { selectedType, options, getTranslate, isLoading, chartPreviewContent } = this.props;
    const { isChartPreviewVisible } = this.state;

    if (chartPreviewContent) {
      return (
        <div className='graph-details__chart-settings'>
          <div className='graph-details__label'>
            {getTranslate('analytics.editForm.operations.tab')}
          </div>
          <div className='graph-details__wrapper'>
            {this.settingsForm}
            {chartPreviewContent({ options, selectedType, getTranslate, isChartPreviewVisible })}
            {isLoading && <Loader />}
          </div>
        </div>
      );
    }

    switch (selectedType) {
      case AdvancedAnalyticsEntityType.operations: {
        return (
          <div className='graph-details__chart-settings'>
            <div className='graph-details__label'>
              {getTranslate('analytics.editForm.operations.tab')}
            </div>
            <div className='graph-details__wrapper'>
              {this.settingsForm}
              {this.chartView}
              {isLoading && <Loader />}
            </div>
          </div>
        );
      }
      case AdvancedAnalyticsEntityType.inout: {
        return (
          <div className='graph-details__chart-settings'>
            <div className='graph-details__label'>
              {getTranslate('analytics.editForm.inOut.tab')}
            </div>
            <div className='graph-details__wrapper'>
              {this.settingsForm}
              <InOutTable
                className={TABLE_CLASSNAME}
                data={options as InOutData}
                header={'analytics.editForm.example.inOutTable.label'}
              />
              {isLoading && <Loader />}
            </div>
          </div>
        );
      }
      case AdvancedAnalyticsEntityType.declineReasons: {
        return (
          <div className='graph-details__chart-settings'>
            <div className='graph-details__label'>
              {getTranslate('analytics.editForm.operationsPivot.tab')}
            </div>
            <div className='graph-details__wrapper'>
              {this.settingsForm}
              <InOutTable
                className={TABLE_CLASSNAME}
                data={options as DeclineReasonsData}
                header={'analytics.editForm.example.pivot.label'}
              />
              {isLoading && <Loader />}
            </div>
          </div>
        );
      }
      case AdvancedAnalyticsEntityType.topten: {
        return (
          <div className='graph-details__chart-settings'>
            <div className='graph-details__label'>
              {getTranslate('analytics.editForm.top.tab')}
            </div>
            <div className='graph-details__wrapper'>
              {this.settingsForm}
              {this.chartView}
            </div>
            {isLoading && <Loader />}
          </div>
        );
      }
      case AdvancedAnalyticsEntityType.chargeback: {
        return (
          <div className='graph-details__chart-settings'>
            <div className='graph-details__label'>
              {getTranslate('analytics.editForm.chargeback.tab')}
            </div>
            {this.renderChargeAndFraudAnalytics()}
          </div>
        );
      }
      case AdvancedAnalyticsEntityType.fraud_report: {
        return (
          <div className='graph-details__chart-settings'>
            <div className='graph-details__label'>
              {getTranslate('analytics.editForm.fraud.tab')}
            </div>
            {this.renderChargeAndFraudAnalytics()}
          </div>
        );
      }
      case AdvancedAnalyticsEntityType.success_rate: {
        return (
          <div className='graph-details__chart-settings'>
            <div className='graph-details__label'>
              {getTranslate('analytics.editForm.successRate.tab')}
            </div>
            {this.renderChargeAndFraudAnalytics()}
          </div>
        );
      }
      case AdvancedAnalyticsEntityType.vat_analytics: {
        return (
          <div className='graph-details__chart-settings'>
            <div className='graph-details__label'>
              {getTranslate('analytics.editForm.tax.tab')}
            </div>
            {this.renderChargeAndFraudAnalytics()}
          </div>
        );
      }
      default: {
        return <NoContent />;
      }
    }
  }

  renderChargeAndFraudAnalytics = () => {
    const { values, options, isLoading } = this.props;

    return (
      <div className='graph-details__wrapper'>
        {this.settingsForm}
        {options &&
          'chartType' in values &&
          values.chartType === 'simple_table' &&
          'data' in options ? (
          <AdvancedAnalyticsTable
            className={TABLE_CLASSNAME}
            data={options}
            header={'analytics.editForm.example.table.label'}
          />
        ) : (
          this.chartView
        )}
        {isLoading && <Loader />}
      </div>
    );
  };

  renderContent() {
    const { selectedType, setType, chartTypes } = this.props;

    return (
      <>
        <AdvancedAnalyticsEntityTypes
          selectedType={selectedType}
          setType={setType}
          chartTypes={chartTypes}
        />
        {this.whatToRender()}
      </>
    );
  }
}

const GraphDetails_ = addTranslation(GraphDetails);
export { GraphDetails_ as GraphDetails };
